export type SubtitleWord = {
  id: string;
  text: string;
  start: number;
  end: number;
  speakerId?: string | null;
  furigana?: string;
  romaji?: string;
};

export type SubtitleGroup = {
  id: string;
  index: number;
  subtitle: string;
  sourceText?: string;
  start: number;
  end: number;
  speakerId?: string | null;
  words: SubtitleWord[];
};

export type TimelinePayload = {
  version?: number;
  kind?: string;
  groupMode?: string;
  group_mode?: string;
  karaoke_mode?: boolean;
  show_scrolling_subtitles?: boolean;
  show_timeline_timestamp?: boolean;
  groups?: SubtitleGroup[];
  asr_groups?: SubtitleGroup[];
  speakers?: Array<{
    id: string;
    label?: string;
  }>;
  extensions?: Record<string, unknown>;
};

export function extractGroups(timeline: TimelinePayload) {
  const groups = timeline.groups || timeline.asr_groups || [];
  return groups.map((group) => ({
    ...group,
    speakerId:
      group.speakerId ?? (group as SubtitleGroup & { speaker_id?: string | null }).speaker_id ?? null,
    sourceText:
      group.sourceText ??
      (group as SubtitleGroup & { source_text?: string }).source_text,
    words: (group.words || []).map((word) => ({
      ...word,
      speakerId:
        word.speakerId ??
        (word as SubtitleWord & { speaker_id?: string | null }).speaker_id ??
        null,
    })),
  }));
}

export function getCurrentGroupIndex(
  groups: SubtitleGroup[],
  currentTime: number,
) {
  const exactIndex = groups.findIndex(
    (group) => currentTime >= group.start && currentTime <= group.end,
  );
  if (exactIndex >= 0) return exactIndex;

  return groups.findLastIndex((group) => currentTime >= group.start);
}

export function getCurrentWordIndex(group: SubtitleGroup, currentTime: number) {
  const exactIndex = group.words.findIndex(
    (word) => currentTime >= word.start && currentTime <= word.end,
  );
  if (exactIndex >= 0) return exactIndex;

  return group.words.findLastIndex((word) => currentTime >= word.start);
}

export function computeCompletionStatus(params: {
  totalDuration: number;
  effectiveSeconds: number;
  segmentProgress: Array<{ segmentId: string; fullyCompleted: boolean }>;
  totalSegments: number;
}) {
  const completedSegments = new Set(
    params.segmentProgress
      .filter((segment) => segment.fullyCompleted)
      .map((segment) => segment.segmentId),
  );

  const coverage =
    params.totalSegments === 0
      ? 0
      : completedSegments.size / params.totalSegments;

  const timeReached =
    params.totalDuration === 0
      ? false
      : params.effectiveSeconds >= params.totalDuration * 0.8;

  return {
    completed: coverage >= 1 && timeReached,
    coverage,
    timeReached,
    completedSegments: completedSegments.size,
  };
}
